import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import DevDetails from './DevDetails';
import Leaderboard from './Leaderboard';
import DexPage from './DexPage';
import ActiveDevPage from './ActiveDevPage';
import FAQ from './FAQ';
import APIPage from './APIPage';
import CoinDetails from './CoinDetails';
import Header from './Header';
import './App.css';

const API_BASE = window.location.origin;
const SOCKET_URL = window.location.origin;

function HomePage() {
  const navigate = useNavigate();
  const [launches, setLaunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const launchesRef = useRef(new Map());
  
  // Dev-Daten Cache (creator -> devData)
  const [devDataCache, setDevDataCache] = useState(new Map());
  const devDataCacheRef = useRef(new Map());
  const loadingCreatorsRef = useRef(new Set()); // Verhindert doppelte Requests
  const requestQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  
  // Synchronisiere Ref mit State
  useEffect(() => {
    devDataCacheRef.current = devDataCache;
  }, [devDataCache]);
  
  // Sort State - Buttons statt Dropdowns
  const [sortByLeft, setSortByLeft] = useState('age'); // 'age' or 'marketcap'
  const [sortOrderLeft, setSortOrderLeft] = useState('desc'); // 'asc' or 'desc'
  const [sortByRight, setSortByRight] = useState('age');
  const [sortOrderRight, setSortOrderRight] = useState('desc');
  
  // Mobile Tab State
  const [mobileTab, setMobileTab] = useState('new'); // 'new' or 'alive'

  // Initial Load
  useEffect(() => {
    loadLaunches();
    setupSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  // Socket Setup
  useEffect(() => {
    if (socket) {
      socket.on('connect', () => {
        const now = new Date().toISOString();
        console.log(`[${now}] [SOCKET] ✅ Connected to server`);
        setIsConnected(true);
      });

      socket.on('disconnect', () => {
        const now = new Date().toISOString();
        console.log(`[${now}] [SOCKET] ❌ Disconnected from server`);
        setIsConnected(false);
      });

      socket.on('new-dev-launch', (newLaunches) => {
        const now = new Date().toISOString();
        console.log(`[${now}] [SOCKET] 🚀 New Dev Launch empfangen: ${newLaunches?.length || 0} neue Coins`);
        handleNewLaunches(newLaunches);
      });

      socket.on('market-cap-update', (update) => {
        const now = new Date().toISOString();
        console.log(`[${now}] [SOCKET] 📡 Market Cap Update empfangen: ${update.tokenmint?.substring(0, 8) || 'UNKNOWN'}... | MC: ${update.currentmarketcapsol} SOL ($${update.currentmarketcapsol_usd}) | Peak: ${update.peakmarketcapsol} SOL ($${update.peakmarketcapsol_usd})`);
        handleMarketCapUpdate(update);
      });

      socket.on('dex-status-update', (update) => {
        const now = new Date().toISOString();
        console.log(`[${now}] [SOCKET] 📡 DEX Status Update empfangen: ${update.tokenmint?.substring(0, 8) || 'UNKNOWN'}... | Status: ${update.dex_status}`);
        handleDexStatusUpdate(update);
      });

      socket.on('image-update', (update) => {
        const now = new Date().toISOString();
        console.log(`[${now}] [SOCKET] 🖼️ Image Update empfangen: ${update.tokenmint?.substring(0, 8) || 'UNKNOWN'}... | Image: ${update.imageurl?.substring(0, 50) || 'N/A'}...`);
        handleImageUpdate(update);
      });

      return () => {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('new-dev-launch');
        socket.off('market-cap-update');
        socket.off('dex-status-update');
        socket.off('image-update');
      };
    }
  }, [socket]);

  const setupSocket = () => {
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    setSocket(newSocket);
  };

  const loadLaunches = async (retryCount = 0) => {
    try {
      setError(null);
      const response = await axios.get(`/api/dev-launches/realtime?t=${Date.now()}`, {
        timeout: 30000,
        validateStatus: function (status) {
          return status >= 200 && status < 300;
        }
      });
      
      const launchesData = response.data.data || response.data;
      
      if (launchesData && Array.isArray(launchesData)) {
        setLaunches(launchesData);
        launchesRef.current.clear();
        launchesData.forEach(launch => {
          launchesRef.current.set(launch.tokenmint, launch);
        });
        setLoading(false);
        
        // Lade Dev-Daten für alle Creator SOFORT (initial beim Reload)
        // Für initiales Laden: Schnelleres Throttling und mehr Parallelität
        const uniqueCreators = [...new Set(launchesData.map(l => l.creator).filter(Boolean))];
        // Lade Dev-Daten mit schnellerem Staggering (50ms statt 200ms)
        uniqueCreators.forEach((creator, idx) => {
          setTimeout(() => {
            loadDevData(creator);
          }, idx * 50); // Schnelleres Staggering für initiales Laden
        });
      } else {
        throw new Error('Invalid data format');
      }
    } catch (err) {
      console.error('[API] Error:', err);
      if ((err.code === 'ERR_NETWORK' || err.message.includes('CONTENT_LENGTH_MISMATCH') || err.code === 'ERR_CONTENT_LENGTH_MISMATCH') && retryCount < 3) {
        console.log(`[API] Retrying... (${retryCount + 1}/3)`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return loadLaunches(retryCount + 1);
      }
      setError(err.message);
      setLoading(false);
    }
  };

  const handleNewLaunches = (newLaunches) => {
    setLaunches(prevLaunches => {
      const existingMints = new Set(prevLaunches.map(l => l.tokenmint));
      const trulyNew = newLaunches.filter(l => !existingMints.has(l.tokenmint));
      
      if (trulyNew.length === 0) return prevLaunches;
      
      const updated = [...trulyNew, ...prevLaunches];
      trulyNew.forEach(launch => {
        launchesRef.current.set(launch.tokenmint, launch);
      });
      
      return updated;
    });
    
    // Lade Dev-Daten für neue Launches (im Hintergrund, nicht blockierend)
    newLaunches.forEach(launch => {
      if (launch.creator) {
        loadDevData(launch.creator);
      }
    });
  };

  const handleMarketCapUpdate = (update) => {
    setLaunches(prevLaunches => {
      const found = prevLaunches.find(l => l.tokenmint === update.tokenmint);
      if (!found) return prevLaunches;
      
      return prevLaunches.map(launch => {
        if (launch.tokenmint === update.tokenmint) {
          const updated = {
            ...launch,
            currentmarketcapsol: update.currentmarketcapsol,
            currentmarketcapsol_usd: update.currentmarketcapsol_usd,
            peakmarketcapsol: update.peakmarketcapsol,
            peakmarketcapsol_usd: update.peakmarketcapsol_usd,
            _updateAnimation: true
          };
          
          launchesRef.current.set(update.tokenmint, updated);
          
          setTimeout(() => {
            setLaunches(prev => prev.map(l => {
              if (l.tokenmint === update.tokenmint && l._updateAnimation) {
                const { _updateAnimation, ...rest } = l;
                return rest;
              }
              return l;
            }));
          }, 500);
          
          return updated;
        }
        return launch;
      });
    });
  };

  const handleDexStatusUpdate = (update) => {
    setLaunches(prevLaunches => {
      const found = prevLaunches.find(l => l.tokenmint === update.tokenmint);
      if (!found) return prevLaunches;
      
      return prevLaunches.map(launch => {
        if (launch.tokenmint === update.tokenmint) {
          const updated = {
            ...launch,
            dex_status: update.dex_status,
            dex_payment_timestamp: update.dex_payment_timestamp,
            dex_check_timestamp: update.dex_check_timestamp,
            dex_payment_time_ago: update.dex_payment_time_ago
          };
          
          launchesRef.current.set(update.tokenmint, updated);
          return updated;
        }
        return launch;
      });
    });
  };

  const handleImageUpdate = (update) => {
    setLaunches(prevLaunches => {
      const found = prevLaunches.find(l => l.tokenmint === update.tokenmint);
      if (!found) return prevLaunches;
      
      // Nur updaten wenn Coin noch kein Bild hat oder Bild sich geändert hat
      if (found.imageurl && found.imageurl === update.imageurl) {
        return prevLaunches; // Bild bereits vorhanden und unverändert
      }
      
      return prevLaunches.map(launch => {
        if (launch.tokenmint === update.tokenmint) {
          const updated = {
            ...launch,
            imageurl: update.imageurl
          };
          
          launchesRef.current.set(update.tokenmint, updated);
          
          return updated;
        }
        return launch;
      });
    });
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
    const diff = now - time;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours % 24} hour${(hours % 24) > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes % 60} min ago`;
    } else if (minutes > 0) {
      return `${minutes} min ago`;
    } else {
      return `${seconds} sec ago`;
    }
  };

  const formatNumber = (num) => {
    const value = parseFloat(num);
    if (value >= 1000000) {
      return (value / 1000000).toFixed(2) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(2) + 'K';
    }
    return value.toFixed(2);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Lade Dev-Daten für einen Creator (mit Queue und Throttling)
  const loadDevData = async (creator) => {
    if (!creator || devDataCacheRef.current.has(creator) || loadingCreatorsRef.current.has(creator)) {
      return; // Bereits geladen, geladen wird gerade, oder kein Creator
    }
    
    // Füge zur Queue hinzu
    if (!requestQueueRef.current.includes(creator)) {
      requestQueueRef.current.push(creator);
    }
    
    // Verarbeite Queue (nur wenn nicht bereits in Bearbeitung)
    if (!isProcessingQueueRef.current) {
      processRequestQueue();
    }
  };
  
  // Verarbeite Request-Queue mit Throttling
  const processRequestQueue = async () => {
    if (isProcessingQueueRef.current || requestQueueRef.current.length === 0) {
      return;
    }
    
    isProcessingQueueRef.current = true;
    
    while (requestQueueRef.current.length > 0) {
      const creator = requestQueueRef.current.shift();
      
      // Skip wenn bereits geladen oder wird gerade geladen
      if (devDataCacheRef.current.has(creator) || loadingCreatorsRef.current.has(creator)) {
        continue;
      }
      
      loadingCreatorsRef.current.add(creator);
      
      try {
        const response = await axios.get(`${API_BASE}/api/dev/${encodeURIComponent(creator)}?t=${Date.now()}`, {
          timeout: 5000
        });
        
        if (response.data && response.data.stats) {
          const devData = {
            best_coin: response.data.stats.best_coin || null,
            coins: (response.data.coins || []).slice(0, 10) // Nur letzte 10
          };
          
          // Update Ref sofort
          devDataCacheRef.current.set(creator, devData);
          
          // Update State (triggert Re-Render nur wenn nötig)
          setDevDataCache(prev => {
            if (prev.has(creator)) return prev; // Bereits vorhanden
            const newMap = new Map(prev);
            newMap.set(creator, devData);
            return newMap;
          });
        }
      } catch (err) {
        // Ignore errors - Dev-Daten sind optional
        console.log(`[DEV DATA] Could not load dev data for ${creator.substring(0, 8)}...`);
      } finally {
        loadingCreatorsRef.current.delete(creator);
      }
      
      // Schnelleres Throttling für initiales Laden (50ms statt 200ms)
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    isProcessingQueueRef.current = false;
  };

  // Bestimme Icon-Farbe basierend auf ATH Marketcap
  const getCoinIconColor = (athMC) => {
    const ath = parseFloat(athMC || 0);
    if (ath >= 60000) return 'green-dark'; // Dunkelgrün
    if (ath >= 20000) return 'green-light'; // Hellgrün
    if (ath >= 10000) return 'yellow'; // Gelb
    return 'red'; // Rot
  };

  // Memoized Icon-Komponente - verhindert Re-Renders
  const CoinIcon = React.memo(({ coin, colorClass, onNavigate }) => {
    return (
      <div
        className={`dev-coin-icon ${colorClass}`}
        onClick={(e) => {
          e.stopPropagation();
          if (coin.tokenmint) {
            onNavigate(`/coin/${coin.tokenmint}`);
          }
        }}
        title={`${coin.symbol || 'N/A'}: ATH $${formatNumber(coin.peakmarketcapsol_usd)}`}
      >
        {coin.symbol ? coin.symbol.substring(0, 3).toUpperCase() : '?'}
      </div>
    );
  }, (prevProps, nextProps) => {
    // Nur neu rendern wenn sich tokenmint oder symbol ändert
    return prevProps.coin.tokenmint === nextProps.coin.tokenmint &&
           prevProps.coin.symbol === nextProps.coin.symbol &&
           prevProps.colorClass === nextProps.colorClass;
  });

  // Dev-Info-Kasten Komponente - Komplett statisch wie Symbol, DEX Status etc.
  // Liest Daten direkt aus launch Objekt - KEINE separaten API-Calls, KEINE State-Updates
  const DevInfoBox = React.memo(({ launch }) => {
    // Verwende Daten direkt aus launch Objekt (wie launch.symbol, launch.dex_status)
    const bestCoin = launch.dev_best_coin || null;
    
    // Hole Icons aus Cache (nur lesen, kein State-Update)
    const cachedData = devDataCacheRef.current.get(launch.creator);
    const coins = cachedData?.coins || [];
    const coinIcons = coins.length > 0 
      ? coins
          .filter(coin => coin.tokenmint !== launch.tokenmint)
          .map((coin) => ({
            key: coin.tokenmint,
            colorClass: getCoinIconColor(coin.peakmarketcapsol_usd),
            coin: {
              tokenmint: coin.tokenmint,
              symbol: coin.symbol,
              peakmarketcapsol_usd: coin.peakmarketcapsol_usd
            }
          }))
      : [];
    
    // Lade Dev-Daten im Hintergrund (auch wenn bereits im Cache für Updates)
    useEffect(() => {
      if (launch.creator) {
        loadDevData(launch.creator);
      }
    }, [launch.creator]);
    
    // Zeige Box nur wenn bestCoin vorhanden ist (wie bei anderen statischen Elementen)
    if (!bestCoin) {
      return null;
    }
    
    return (
      <div className="dev-info-box">
        <div className="dev-info-content">
          <div className="dev-info-left">
            <div className="dev-info-line1">
              <span className="dev-info-label">BEST COIN:</span>
              <span className="dev-info-symbol">{bestCoin.symbol}</span>
              <span 
                className="dev-info-tokenmint clickable"
                onClick={(e) => {
                  e.stopPropagation();
                  // Tokenmint aus best_coin - falls nicht vorhanden, verwende den ersten Coin
                  const tokenmint = bestCoin.tokenmint || (coinIcons[0]?.coin?.tokenmint) || '';
                  if (tokenmint) {
                    copyToClipboard(tokenmint);
                  }
                }}
                title="Click to copy tokenmint"
              >
                ({bestCoin.tokenmint ? `${bestCoin.tokenmint.substring(0, 8)}...${bestCoin.tokenmint.substring(bestCoin.tokenmint.length - 4)}` : (coinIcons[0]?.coin?.tokenmint ? `${coinIcons[0].coin.tokenmint.substring(0, 8)}...${coinIcons[0].coin.tokenmint.substring(coinIcons[0].coin.tokenmint.length - 4)}` : 'N/A')})
              </span>
              <span className="dev-info-separator">:</span>
              <span className="dev-info-label">ATH:</span>
              <span className="dev-info-ath">{formatNumber(bestCoin.peakmarketcapsol_usd)}</span>
            </div>
            <div className="dev-info-line2">
              <span className="dev-info-launched">Launched {formatTimeAgo(bestCoin.created_at_timestamp || bestCoin.created_at)}</span>
            </div>
          </div>
          <div className="dev-info-right">
            {coinIcons.length > 0 ? (
              coinIcons.map(({ key, colorClass, coin }) => (
                <CoinIcon
                  key={key}
                  coin={coin}
                  colorClass={colorClass}
                  onNavigate={navigate}
                />
              ))
            ) : null}
          </div>
        </div>
      </div>
    );
  }, (prevProps, nextProps) => {
    // Nur neu rendern wenn creator oder tokenmint sich ändert
    // Ignoriere ALLE anderen Änderungen (Marketcap, etc.)
    const creatorChanged = prevProps.launch.creator !== nextProps.launch.creator;
    const tokenmintChanged = prevProps.launch.tokenmint !== nextProps.launch.tokenmint;
    
    // Wenn sich nichts Relevantes geändert hat, KEIN Re-Render
    // true = keine Änderung, kein Re-Render
    // false = Änderung erkannt, Re-Render nötig
    return !creatorChanged && !tokenmintChanged;
  });

  const toggleSort = (column, side) => {
    if (side === 'left') {
      if (sortByLeft === column) {
        setSortOrderLeft(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setSortByLeft(column);
        setSortOrderLeft('desc');
      }
    } else {
      if (sortByRight === column) {
        setSortOrderRight(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setSortByRight(column);
        setSortOrderRight('desc');
      }
    }
  };

  const sortLaunches = (launches, sortBy, sortOrder) => {
    return [...launches].sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'age') {
        aValue = a.created_at_timestamp || new Date(a.created_at).getTime();
        bValue = b.created_at_timestamp || new Date(b.created_at).getTime();
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      } else if (sortBy === 'marketcap') {
        aValue = parseFloat(a.currentmarketcapsol_usd || 0);
        bValue = parseFloat(b.currentmarketcapsol_usd || 0);
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      }
      
      return 0;
    });
  };

  // Left Column: NEW DEV LAUNCHES (alle mit bonding rate >= 5% UND maximal 24 Stunden alt)
  const getNewDevLaunches = () => {
    const now = Date.now();
    const filtered = launches.filter(launch => {
      const bondingRate = parseFloat(launch.bonding_rate_percent || 0);
      if (bondingRate < 5) return false;
      
      // Maximal 24 Stunden alt
      const createdTime = launch.created_at_timestamp || new Date(launch.created_at).getTime();
      const ageHours = (now - createdTime) / (1000 * 60 * 60);
      if (ageHours > 24) return false;
      
      return true;
    });
    return sortLaunches(filtered, sortByLeft, sortOrderLeft).slice(0, 30);
  };

  // Right Column: STILL ALIVE (mindestens 5 min alt UND current MC > 8K USD UND maximal 4 Tage alt)
  const getStillAliveLaunches = () => {
    const now = Date.now();
    const filtered = launches.filter(launch => {
      const bondingRate = parseFloat(launch.bonding_rate_percent || 0);
      if (bondingRate < 5) return false;
      
      const createdTime = launch.created_at_timestamp || new Date(launch.created_at).getTime();
      const ageHours = (now - createdTime) / (1000 * 60 * 60);
      
      // Mindestens 5 Minuten alt
      const ageMinutes = (now - createdTime) / (1000 * 60);
      if (ageMinutes < 5) return false;
      
      // Maximal 4 Tage alt (96 Stunden)
      if (ageHours > 96) return false;
      
      const currentMC = parseFloat(launch.currentmarketcapsol_usd || 0);
      return currentMC > 8000;
    });
    return sortLaunches(filtered, sortByRight, sortOrderRight).slice(0, 30);
  };

  const newDevLaunches = getNewDevLaunches();
  const stillAliveLaunches = getStillAliveLaunches();

  return (
    <div className="App">
      <Header />
      
      <main className="app-main">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading Dev Launches...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>❌ Error: {error}</p>
            <button onClick={loadLaunches} className="retry-btn">
              Retry
            </button>
          </div>
        ) : launches.length === 0 ? (
          <div className="empty-container">
            <p>No Dev Launches found</p>
          </div>
        ) : (
          <>
            <div className="stats-bar">
              <span>Real-time updates active</span>
            </div>
            
            {/* Mobile Tabs */}
            <div className="mobile-tabs">
              <button 
                className={`mobile-tab ${mobileTab === 'new' ? 'active' : ''}`}
                onClick={() => setMobileTab('new')}
              >
                NEW DEV LAUNCHES
              </button>
              <button 
                className={`mobile-tab ${mobileTab === 'alive' ? 'active' : ''}`}
                onClick={() => setMobileTab('alive')}
              >
                STILL ALIVE
              </button>
            </div>
            
            <div className="two-column-layout">
              {/* Left Column: NEW DEV LAUNCHES */}
              <div className={`column-left ${mobileTab === 'new' ? 'mobile-active' : 'mobile-hidden'}`}>
                <div className="column-header">
                  <h2>NEW DEV LAUNCHES</h2>
                  <div className="sort-buttons">
                    <button 
                      className={`sort-btn ${sortByLeft === 'age' ? 'active' : ''}`}
                      onClick={() => toggleSort('age', 'left')}
                    >
                      AGE {sortByLeft === 'age' && (sortOrderLeft === 'asc' ? '↑' : '↓')}
                    </button>
                    <button 
                      className={`sort-btn ${sortByLeft === 'marketcap' ? 'active' : ''}`}
                      onClick={() => toggleSort('marketcap', 'left')}
                    >
                      MARKETCAP {sortByLeft === 'marketcap' && (sortOrderLeft === 'asc' ? '↑' : '↓')}
                    </button>
                  </div>
                </div>
                
                <div className="launches-list">
                  {newDevLaunches.map((launch) => (
                    <div 
                      key={launch.tokenmint} 
                      className="launch-row clickable-row"
                      onClick={() => navigate(`/coin/${launch.tokenmint}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Desktop Layout */}
                      <div className="desktop-layout">
                        <div className="row-time">
                          <div className="row-time-left">
                            {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                            {launch.imageurl && (
                              <img 
                                src={launch.imageurl} 
                                alt={launch.symbol || launch.name || 'Coin'}
                                className="coin-image-small"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                          </div>
                        </div>
                        
                        <div className="row-symbol">
                          <div className="symbol-name">{launch.symbol || launch.name || 'N/A'}</div>
                          <div 
                            className="row-ca clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(launch.tokenmint);
                            }}
                            title="Click to copy"
                          >
                            {launch.tokenmint.substring(0, 8)}...{launch.tokenmint.substring(launch.tokenmint.length - 4)}
                          </div>
                        </div>
                        
                        <div className="row-dex">
                          {(() => {
                            const dexStatus = launch.dex_status;
                            if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                              return (
                                <div className={`dex-badge-small ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                                  <div>DEX PAID</div>
                                  {launch.dex_payment_time_ago && (
                                    <div className="dex-payment-time">{launch.dex_payment_time_ago}</div>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div className="dex-badge-small dex-not-paid">
                                DEX NOT PAID
                              </div>
                            );
                          })()}
                        </div>
                        
                        <div className="row-dev">
                          <div className="dev-label">DEV</div>
                          <div 
                            className="dev-ca clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dev/${encodeURIComponent(launch.creator)}`);
                            }}
                            title="Click to view dev details"
                          >
                            {launch.creator.substring(0, 8)}...{launch.creator.substring(launch.creator.length - 4)}
                          </div>
                          <div className="bonding-rate-small">{launch.bonding_rate_percent}%</div>
                        </div>
                        
                        <div className="row-actions">
                          <a 
                            href={`https://pump.fun/coin/${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-btn-small pump-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            🚀 Pump.fun
                          </a>
                          <a 
                            href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-btn-small bullx-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📊 BullX
                          </a>
                        </div>
                        
                        <div className="row-mc">
                          <div className="mc-label-small">CURRENT MC</div>
                          <div className="mc-value-large green">{formatNumber(launch.currentmarketcapsol_usd)}</div>
                          <div className="ath-label-small">ATH</div>
                          <div className="ath-value-medium">{formatNumber(launch.peakmarketcapsol_usd)}</div>
                        </div>
                        
                        {/* Dev Info Box */}
                        <DevInfoBox launch={launch} />
                      </div>
                      
                      {/* Mobile Layout */}
                      <div className="mobile-layout">
                        {/* Ganz oben: Zeit */}
                        <div className="mobile-time-top">
                          {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                        </div>
                        
                        {/* Bild */}
                        {launch.imageurl && (
                          <div className="mobile-image-container">
                            <img 
                              src={launch.imageurl} 
                              alt={launch.symbol || launch.name || 'Coin'}
                              className="coin-image-mobile"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Symbol */}
                        <div className="mobile-symbol">
                          {launch.symbol || launch.name || 'N/A'}
                        </div>
                        
                        {/* Tokenmint - größer */}
                        <div 
                          className="mobile-tokenmint clickable"
                          onClick={() => copyToClipboard(launch.tokenmint)}
                          title="Click to copy"
                        >
                          {launch.tokenmint}
                        </div>
                        
                        {/* MC und ATH nebeneinander, je 50% */}
                        <div className="mobile-mc-ath-row">
                          <div className="mobile-mc-box">
                            <div className="mobile-mc-label">Current MC</div>
                            <div className="mobile-mc-value">{formatNumber(launch.currentmarketcapsol_usd)}</div>
                          </div>
                          <div className="mobile-ath-box">
                            <div className="mobile-ath-label">ATH</div>
                            <div className="mobile-ath-value">{formatNumber(launch.peakmarketcapsol_usd)}</div>
                          </div>
                        </div>
                        
                        {/* DEV und DEX nebeneinander, je 50% */}
                        <div className="mobile-dev-dex-row">
                          <div className="mobile-dev-box">
                            <div className="mobile-dev-label">DEV</div>
                            <div 
                              className="mobile-dev-ca clickable"
                              onClick={() => navigate(`/dev/${encodeURIComponent(launch.creator)}`)}
                              title="Click to view dev details"
                            >
                              {launch.creator}
                            </div>
                            <div className="mobile-bonding-rate">{launch.bonding_rate_percent}%</div>
                          </div>
                          <div className="mobile-dex-box">
                            {(() => {
                              const dexStatus = launch.dex_status;
                              if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                                return (
                                  <div className={`mobile-dex-badge ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                                    <div>DEX PAID</div>
                                    {launch.dex_payment_time_ago && (
                                      <div className="dex-payment-time">{launch.dex_payment_time_ago}</div>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <div className="mobile-dex-badge dex-not-paid">
                                  DEX NOT PAID
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        
                        {/* Buttons nebeneinander */}
                        <div className="mobile-actions-row">
                          <a 
                            href={`https://pump.fun/coin/${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mobile-action-btn pump-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            🚀 Pump.fun
                          </a>
                          <a 
                            href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mobile-action-btn bullx-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📊 BullX
                          </a>
                        </div>
                        
                        {/* Dev Info Box (Mobile) */}
                        <DevInfoBox launch={launch} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right Column: STILL ALIVE */}
              <div className={`column-right ${mobileTab === 'alive' ? 'mobile-active' : 'mobile-hidden'}`}>
                <div className="column-header">
                  <h2>STILL ALIVE</h2>
                  <div className="sort-buttons">
                    <button 
                      className={`sort-btn ${sortByRight === 'age' ? 'active' : ''}`}
                      onClick={() => toggleSort('age', 'right')}
                    >
                      AGE {sortByRight === 'age' && (sortOrderRight === 'asc' ? '↑' : '↓')}
                    </button>
                    <button 
                      className={`sort-btn ${sortByRight === 'marketcap' ? 'active' : ''}`}
                      onClick={() => toggleSort('marketcap', 'right')}
                    >
                      MARKETCAP {sortByRight === 'marketcap' && (sortOrderRight === 'asc' ? '↑' : '↓')}
                    </button>
                  </div>
                </div>
                
                <div className="launches-list">
                  {stillAliveLaunches.map((launch) => (
                    <div 
                      key={launch.tokenmint} 
                      className="launch-row clickable-row"
                      onClick={() => navigate(`/coin/${launch.tokenmint}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Desktop Layout */}
                      <div className="desktop-layout">
                        <div className="row-time">
                          <div className="row-time-left">
                            {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                            {launch.imageurl && (
                              <img 
                                src={launch.imageurl} 
                                alt={launch.symbol || launch.name || 'Coin'}
                                className="coin-image-small"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                          </div>
                        </div>
                        
                        <div className="row-symbol">
                          <div className="symbol-name">{launch.symbol || launch.name || 'N/A'}</div>
                          <div 
                            className="row-ca clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(launch.tokenmint);
                            }}
                            title="Click to copy"
                          >
                            {launch.tokenmint.substring(0, 8)}...{launch.tokenmint.substring(launch.tokenmint.length - 4)}
                          </div>
                        </div>
                        
                        <div className="row-dex">
                          {(() => {
                            const dexStatus = launch.dex_status;
                            if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                              return (
                                <div className={`dex-badge-small ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                                  <div>DEX PAID</div>
                                  {launch.dex_payment_time_ago && (
                                    <div className="dex-payment-time">{launch.dex_payment_time_ago}</div>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div className="dex-badge-small dex-not-paid">
                                DEX NOT PAID
                              </div>
                            );
                          })()}
                        </div>
                        
                        <div className="row-dev">
                          <div className="dev-label">DEV</div>
                          <div 
                            className="dev-ca clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dev/${encodeURIComponent(launch.creator)}`);
                            }}
                            title="Click to view dev details"
                          >
                            {launch.creator.substring(0, 8)}...{launch.creator.substring(launch.creator.length - 4)}
                          </div>
                          <div className="bonding-rate-small">{launch.bonding_rate_percent}%</div>
                        </div>
                        
                        <div className="row-actions">
                          <a 
                            href={`https://pump.fun/coin/${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-btn-small pump-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            🚀 Pump.fun
                          </a>
                          <a 
                            href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-btn-small bullx-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📊 BullX
                          </a>
                        </div>
                        
                        <div className="row-mc">
                          <div className="mc-label-small">CURRENT MC</div>
                          <div className="mc-value-large green">{formatNumber(launch.currentmarketcapsol_usd)}</div>
                          <div className="ath-label-small">ATH</div>
                          <div className="ath-value-medium">{formatNumber(launch.peakmarketcapsol_usd)}</div>
                        </div>
                        
                        {/* Dev Info Box */}
                        <DevInfoBox launch={launch} />
                      </div>
                      
                      {/* Mobile Layout */}
                      <div className="mobile-layout">
                        {/* Ganz oben: Zeit */}
                        <div className="mobile-time-top">
                          {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                        </div>
                        
                        {/* Bild */}
                        {launch.imageurl && (
                          <div className="mobile-image-container">
                            <img 
                              src={launch.imageurl} 
                              alt={launch.symbol || launch.name || 'Coin'}
                              className="coin-image-mobile"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Symbol */}
                        <div className="mobile-symbol">
                          {launch.symbol || launch.name || 'N/A'}
                        </div>
                        
                        {/* Tokenmint - größer */}
                        <div 
                          className="mobile-tokenmint clickable"
                          onClick={() => copyToClipboard(launch.tokenmint)}
                          title="Click to copy"
                        >
                          {launch.tokenmint}
                        </div>
                        
                        {/* MC und ATH nebeneinander, je 50% */}
                        <div className="mobile-mc-ath-row">
                          <div className="mobile-mc-box">
                            <div className="mobile-mc-label">Current MC</div>
                            <div className="mobile-mc-value">{formatNumber(launch.currentmarketcapsol_usd)}</div>
                          </div>
                          <div className="mobile-ath-box">
                            <div className="mobile-ath-label">ATH</div>
                            <div className="mobile-ath-value">{formatNumber(launch.peakmarketcapsol_usd)}</div>
                          </div>
                        </div>
                        
                        {/* DEV und DEX nebeneinander, je 50% */}
                        <div className="mobile-dev-dex-row">
                          <div className="mobile-dev-box">
                            <div className="mobile-dev-label">DEV</div>
                            <div 
                              className="mobile-dev-ca clickable"
                              onClick={() => navigate(`/dev/${encodeURIComponent(launch.creator)}`)}
                              title="Click to view dev details"
                            >
                              {launch.creator}
                            </div>
                            <div className="mobile-bonding-rate">{launch.bonding_rate_percent}%</div>
                          </div>
                        <div className="mobile-dex-box">
                          {(() => {
                            const dexStatus = launch.dex_status;
                            if (dexStatus === 'PENDING' || dexStatus === 'APPROVED' || dexStatus === 'PAID') {
                              return (
                                <div className={`mobile-dex-badge ${dexStatus === 'APPROVED' || dexStatus === 'PAID' ? 'dex-paid' : 'dex-pending'}`}>
                                  <div>DEX PAID</div>
                                  {launch.dex_payment_time_ago && (
                                    <div className="dex-payment-time">{launch.dex_payment_time_ago}</div>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div className="mobile-dex-badge dex-not-paid">
                                DEX NOT PAID
                              </div>
                            );
                          })()}
                        </div>
                        </div>
                        
                        {/* Buttons nebeneinander */}
                        <div className="mobile-actions-row">
                          <a 
                            href={`https://pump.fun/coin/${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mobile-action-btn pump-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            🚀 Pump.fun
                          </a>
                          <a 
                            href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mobile-action-btn bullx-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📊 BullX
                          </a>
                        </div>
                        
                        {/* Dev Info Box (Mobile) */}
                        <DevInfoBox launch={launch} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <footer className="app-footer">
        <p>DevRadar.fun - Real-time Dev Launches Tracker</p>
        <p>Only Devs with Bonding Rate ≥ 5% and more than 1 Coin</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/dev/:creator" element={<DevDetails />} />
      <Route path="/coin/:tokenmint" element={<CoinDetails />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/dex" element={<DexPage />} />
      <Route path="/activedev" element={<ActiveDevPage />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/api-docs" element={<APIPage />} />
    </Routes>
  );
}

export default App;
